import { REWARD_CURSOR_PROGRESSION_DEFAULT_ORDER } from "@config";
import { RewardCursor } from "@domain/User";

export interface RewardCursorProgression {
  cursor: RewardCursor;
  isUnlocked: boolean;
  isNext: boolean;
}

export class RewardCursors {
  private readonly progression: readonly RewardCursor[] = REWARD_CURSOR_PROGRESSION_DEFAULT_ORDER;
  private readonly lastAvailableCursor?: RewardCursor;
  private readonly currentCursor?: RewardCursor;

  constructor(state?: { lastAvailableCursor?: RewardCursor; currentCursor?: RewardCursor }) {
    this.lastAvailableCursor = state?.lastAvailableCursor;
    this.currentCursor = state?.currentCursor;
  }

  getNextCursorToUnlock(): RewardCursor | undefined {
    if (!this.lastAvailableCursor) {
      return this.progression[0];
    }

    const currentPosition = this.progression.indexOf(this.lastAvailableCursor);
    const isLastCursor = currentPosition >= this.progression.length - 1;

    return isLastCursor ? undefined : this.progression[currentPosition + 1];
  }

  getUnlockedCursors(): RewardCursor[] {
    if (!this.lastAvailableCursor) {
      return [];
    }

    const lastAvailableIndex = this.progression.indexOf(this.lastAvailableCursor);
    if (lastAvailableIndex === -1) {
      return [];
    }

    return this.progression.slice(0, lastAvailableIndex + 1) as RewardCursor[];
  }

  getCursorProgressionWithStatus(): RewardCursorProgression[] {
    let hasReachedLastAvailable = !this.lastAvailableCursor;
    const nextCursor = this.getNextCursorToUnlock();

    return this.progression.map(cursor => {
      const isUnlocked = !hasReachedLastAvailable;

      if (cursor === this.lastAvailableCursor) {
        hasReachedLastAvailable = true;
      }

      return {
        cursor,
        isUnlocked,
        isNext: cursor === nextCursor,
      };
    });
  }

  getLastAvailableCursor(): RewardCursor | undefined {
    return this.lastAvailableCursor;
  }

  getCurrentCursor(): RewardCursor | undefined {
    return this.currentCursor;
  }

  hasUnlockedCursors(): boolean {
    if (!this.lastAvailableCursor) return false;

    return this.progression.includes(this.lastAvailableCursor);
  }
}
